<template>
  <div
    class="relative min-w-full bg-cover bg-center h-full flex flex-col items-center justify-center"
    :style="{
      backgroundImage: `url(${require('../assets/images/grafokettbakgrundsbild2.png')})`,
    }"
  >
    <div
      v-if="subscriptionMessage && !showForgotPassword"
      class="w-full max-w-md p-3 mb-4 text-sm text-center whitespace-pre-line rounded bg-warning text-textcolor-dark"
    >
      {{ subscriptionMessage }}
    </div>
    <div class="loginbox mb-2">
      <LoginBox
        v-show="isLoginPage && !showForgotPassword"
        @keyup.enter="nextPage"
      >
        <template v-slot:header>
          <p class="text-3xl">Log in</p>
        </template>
        <template>
          <div class="flex flex-col items-center">
            <div
              class="flex flex-col items-center justify-center mt-6 mb-2 w-full space-y-2"
            >
              <MicrosoftButton @click="() => login('microsoft')" />
              <GoogleButton @click="() => login('google')" />
            </div>
            <HorizontalLineText>
              <span class="text-xs">OR</span>
            </HorizontalLineText>
            <v-input
              @keyup.enter.native="nextPage"
              :labelPlaceholder="'Email'"
              :type="'email'"
              v-model="email"
            />
            <grafokett-button
              class="primary w-full"
              :class="{ disable: email === '' }"
              @click="nextPage"
              >Next</grafokett-button
            >
          </div>
        </template>
      </LoginBox>
      <LoginBox
        v-show="!isLoginPage && !showForgotPassword"
        @keyup.enter="() => login()"
      >
        <template v-slot:header>
          <p>{{ email }}</p>
        </template>
        <template>
          <div class="mt-20">
            <v-input
              ref="passwordInput"
              @keyup.enter.native="() => login()"
              :showPassword="true"
              :error="showErrorMessage(errorMessage)"
              :labelPlaceholder="'Password'"
              :type="'password'"
              v-model="password"
            />
            <div class="relative">
              <grafokett-button
                @click="isLoginPage = !isLoginPage"
                class="small"
                ><i class="arrow left" /> Back</grafokett-button
              >

              <div class="inline-block absolute right-0">
                <grafokett-button
                  class="primary"
                  :class="{ disable: password === '' }"
                  @click="() => login()"
                  >Log in</grafokett-button
                >
              </div>
            </div>
            <grafokett-button
              class="small mt-6"
              :class="{ 'has-error': showErrorMessage(errorMessage) }"
              style="font-style: italic;"
              @click="showForgotPassword = !showForgotPassword"
            >
              Forgot your password?
            </grafokett-button>
          </div>
        </template>
      </LoginBox>
      <LoginBox v-if="showForgotPassword && !isLoginPage">
        <template v-slot:header>
          <h3 class="text-2xl">Forgot your password?</h3>
          <p class="w-1/2 m-auto text-xs w-">
            Let us know what email adress you use to login and we’ll send you an
            email with instructions on how to reset your password
          </p>
        </template>
        <template>
          <v-input
            @keyup.enter.native="sendEmail"
            :labelPlaceholder="'Email'"
            :type="'email'"
            v-model="email"
          />
          <div class="relative flex items-center">
            <grafokett-button
              @click="showForgotPassword = !showForgotPassword"
              class="small"
            >
              <i class="arrow left" /> Back
            </grafokett-button>

            <div class="inline-block absolute right-0">
              <grafokett-button
                class="primary"
                :class="{ disable: email === '' }"
                @click="sendEmail"
              >
                Send
              </grafokett-button>
            </div>
          </div>
        </template>
      </LoginBox>
    </div>
    <Loading
      v-if="loginStatus === 'loading' || loginStatus === 'success'"
      :heading="showForgotPassword ? 'Done' : 'Logging in...'"
      :description="
        showForgotPassword
          ? 'We sent you an email with instructions on how to reset your password.'
          : 'Please wait'
      "
    />
  </div>
</template>

<script>
import vInput from "components/forms/Input.vue";
import GrafokettButton from "components/forms/Button.vue";
import Loading from "../components/Loading";
import LoginBox from "../components/LoginBox";
import MicrosoftButton from "../components/MicrosoftButton";
import GoogleButton from "../components/GoogleButton";
import HorizontalLineText from "../components/HorizontalLineText";

export default {
  name: "Login",
  data() {
    return {
      isLoginPage: true,
      showForgotPassword: false,
      email: "",
      password: "",
    };
  },
  components: {
    vInput,
    GrafokettButton,
    Loading,
    LoginBox,
    MicrosoftButton,
    GoogleButton,
    HorizontalLineText,
  },
  methods: {
    async login(method = "") {
      const loginUser = {
        email: this.email,
        password: this.password,
        method,
      };
      try {
        await this.$store.dispatch("AUTH/LOGIN", loginUser);
        await this.$store.dispatch("ROLES/GET");
        const redirect = this.redirectPath || { name: "home" };
        this.$router.push(redirect);
      } catch {
        this.password = "";
      }
    },
    nextPage() {
      if (this.email !== "") {
        this.isLoginPage = !this.isLoginPage;
        this.password = "";
        this.$nextTick(() => this.$refs.passwordInput.$el.children[0].focus());
      }
    },
    showErrorMessage(error) {
      if (!error) return;
      // Inactive subscription is shown as its own banner (subscriptionMessage
      // below), not as a password-field error. subscriptionStatus is only
      // ever set by the subscription gate (see store/modules/auth.js), so
      // its mere presence means "blocked" - the value itself may still be
      // "Active" (e.g. status Active but endDate passed).
      if (error.subscriptionStatus) return;
      if (error.status === 422) return error.data;
      if (typeof error.message === "string") return error.message;
      if (typeof error.data === "string") return error.data;
      return error.data.errors.Password[0];
    },
    sendEmail() {
      const email = this.email;
      this.$store
        .dispatch("AUTH/FORGOT_PASSWORD", JSON.stringify(email))
        .then(() => {
          this.isLoginPage = true;
          this.showForgotPassword = false;
          this.email = "";
        })
        .catch(err => {
          console.warn("login error", err);
        });
    },
  },
  computed: {
    errorMessage() {
      return this.$store.getters["AUTH/ERROR_MESSAGE"];
    },
    subscriptionMessage() {
      var e = this.errorMessage;
      // See showErrorMessage() - subscriptionStatus's presence, not its
      // value, means the subscription gate blocked this login.
      if (e && e.subscriptionStatus) {
        return e.data;
      }
      return "";
    },
    redirectPath() {
      return this.$store.getters["AUTH/REDIRECT_PATH"];
    },
    loginStatus() {
      return this.$store.getters["AUTH/AUTH_STATUS"];
    },
  },
};
</script>

<style scoped lang="postcss">
.loginbox {
  box-shadow: 0px 0px 10px theme("colors.primary");
  max-width: 34rem;
  @apply w-full bg-primary rounded;
}

.loginbox > * {
  @apply my-12;
}

input {
  @apply mb-6;
}
</style>
